import React from 'react';
import { useState, useEffect } from 'react';
import { marked } from 'marked';
import './styles.css';
import { API_BASE_URL } from '../../config';
import { BotMessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../utils/analytics';

interface Reference {
  text: string;
  url: string;
  date: string;
  title: string;
  tags: Array<{
    level1: string;
    level2: string | null;
    level3: string | null;
  }>;
  similarity: string;
}

interface AIResultProps {
  question: string;
  onSaveNote?: (content: string) => Promise<boolean>;
}

const AIResult: React.FC<AIResultProps> = ({ question, onSaveNote }) => {
  const { t } = useTranslation('airesult');
  const [response, setResponse] = useState('');
  const [parsedResponse, setParsedResponse] = useState('');
  const [references, setReferences] = useState<Reference[]>([]);
  const [status, setStatus] = useState<'thinking' | 'answering' | 'done'>('thinking');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  marked.setOptions({
    breaks: true,
    gfm: true
  });

  useEffect(() => {
    const fetchAIResponse = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ question })
        });

        if (!response.body) {
          logEvent('Command', 'Ask_Response', 'Error_No_Response_Body');
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let responseText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim() && line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'content':
                  responseText += data.content;
                  setResponse(responseText);
                  setStatus('answering');
                  break;
                case 'references':
                  setReferences(data.content);
                  break;
                case 'done':
                  setStatus('done');
                  logEvent('Command', 'Ask_Response', 'Success');
                  break;
              }
            }
          }
        }
      } catch (error) {
        console.error('AI response error:', error);
        logEvent('Command', 'Ask_Response', 'Error');
      }
    };

    fetchAIResponse();
  }, [question]);

  useEffect(() => {
    setParsedResponse(marked.parse(response) as string);
  }, [response]);

  const handleSaveNote = async () => {
    if (isSaving || isSaved) return;
    
    setIsSaving(true);
    if (onSaveNote) {
      const noteContent = `#${t('airesult.ai_dialogue')}\n${t('airesult.question')}: ${question}\n\n${t('airesult.answer')}:\n${response}`;
      try {
        const success = await onSaveNote(noteContent);
        if (success) {
          setIsSaved(true);
          logEvent('Command', 'Ask_Response', 'Saved_As_Note');
        }
      } catch (error) {
        console.error('Failed to save note:', error);
        logEvent('Command', 'Ask_Response', 'Save_Note_Error');
      }
    }
    setIsSaving(false);
  };

  return (
    <div className="chat-result-card">
      <div className="chat-header">
        <div className="ai-chat-header">
          <BotMessageSquare size={20} />
        </div>
        <span className="chat-title">{t('airesult.memowise_ai_assistant')}</span>
        {status !== 'done' && (
          <span className="thinking-status">
            <span className="status-text">
              {status === 'thinking' ? t('airesult.thinking') : t('airesult.answering')}
            </span>
            <span className="status-spinner"></span>
          </span>
        )}
        <span className="chat-question">{question}</span>
      </div>
      <div className="chat-content">
        <div 
          className="ai-response markdown-body"
          dangerouslySetInnerHTML={{ __html: parsedResponse }}
          
        />
        {status === 'done' && (
          <div className="chat-actions">
            <button 
              className={`save-as-note-btn ${isSaved ? 'saved' : ''} ${isSaving ? 'saving' : ''}`}
              onClick={handleSaveNote}
              disabled={isSaving || isSaved}
            >
              {isSaving ? t('airesult.saving') :
               isSaved ? t('airesult.saved') :
               t('airesult.add_to_memowise')}
            </button>
          </div>
        )}
        <div className="reference-section">
          {references.length > 0 && (
            <>
              <div className="reference-title">{t('airesult.reference_content')}</div>
              <div className="reference-list">
                {references.map((ref, index) => (
                  <div key={index} className="reference-item">
                    <div className="reference-meta">
                      <span className="reference-similarity">{t('airesult.similarity')}: {ref.similarity}</span>
                      <span className="reference-date">
                        {new Date(ref.date).toLocaleString()}
                      </span>
                    </div>
                    <div className="reference-text">{ref.text}</div>
                    <div className="reference-footer">
                      <span className="reference-title-text">{ref.title}</span>
                      <a href={ref.url} target="_blank" className="reference-link">
                        {t('airesult.view_original')}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIResult; 